import { useQuery } from '@tanstack/react-query'
import {
  App,
  Button,
  Card,
  Descriptions,
  Empty,
  Flex,
  Tag,
  Typography
} from 'antd'
import { ArrowLeft, Eye, Pencil, Trash2, Zap } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'

import { settingsOutboundWebhooksWebhooksRetrieveOptions } from 'src/api/main'
import {
  ContentSpinner,
  DateTimeCell,
  LabeledTagGrid,
  MonoValue,
  QueryErrorAlert
} from 'src/components'
import {
  useWorkspaceRoute,
  WorkspacePageShell
} from 'src/features/workspaces/pages/WorkspacePageShell'
import { paths } from 'src/router/paths'
import { useShowApiErrors } from 'src/utils'

import {
  useEventCatalogue,
  useOutboundWebhookMutations,
  useSquadAgentUrl,
  useWebhookTest,
  type EventGroup
} from '../../components/AppOutboundWebhooksPanel/hooks'
import { SignatureCard } from '../../components/AppOutboundWebhooksPanel/SignatureCard'
import { SigningSecretModal } from '../../components/AppOutboundWebhooksPanel/SigningSecretModal'
import {
  WebhookFormDrawer,
  type WebhookFormValues
} from '../../components/AppOutboundWebhooksPanel/WebhookFormDrawer'
import { WebhookName } from '../../components/AppOutboundWebhooksPanel/WebhookName'
import { WebhookTestResultModal } from '../../components/AppOutboundWebhooksPanel/WebhookTestResultModal'
import { useWorkspaceAppsList } from '../../hooks/useWorkspaceAppsList'

const { Text } = Typography

/** Roughly the width the events grid below resolves to, so the two cards read as one column. */
const LABEL_WIDTH = 140

// Entity keys are snake_case, so the underscore has to go before capitalising or the fallback
// reads "Opportunity_product". Reachable whenever the catalogue is unavailable.
const titleCase = (value: string) => {
  const spaced = value.replace(/_/g, ' ')

  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/**
 * Groups subscribed event keys by entity and names them the way CRM does.
 *
 * The reason this page exists: the list can only show a count, and "4 events" does not tell an
 * admin whether the ones they care about are covered. Grouping by entity is how the selector
 * presents them, so the two read the same way.
 *
 * Names come from CRM's catalogue, which already carries a localized label per event and per
 * group. When it is unavailable the raw keys are shown instead — degraded, but never blank, and
 * the keys are what appear in the X-CRM-Event header anyway.
 */
const groupEvents = (events: string[], catalogue: EventGroup[] | undefined) => {
  const labels = new Map<string, string>()
  const groupNames = new Map<string, string>()

  for (const group of catalogue ?? []) {
    groupNames.set(group.entity, group.group)
    for (const event of group.events) labels.set(event.key, event.label)
  }

  const grouped = new Map<string, { key: string; label: string }[]>()
  for (const key of [...events].sort()) {
    const entity = key.split('.')[0] ?? key
    grouped.set(entity, [
      ...(grouped.get(entity) ?? []),
      { key, label: labels.get(key) ?? key }
    ])
  }

  return [...grouped.entries()].map(([entity, items]) => ({
    entity,
    name: groupNames.get(entity) ?? titleCase(entity),
    items
  }))
}

export const WorkspaceAppOutboundWebhookDetailPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { message, modal } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const { id, workspaceId, idValid } = useWorkspaceRoute()
  const { appId = '', webhookId = '' } = useParams<{
    appId: string
    webhookId: string
  }>()

  const applicationId = Number(appId) || 0
  const numericWebhookId = Number(webhookId) || 0

  const { apps } = useWorkspaceAppsList(idValid ? workspaceId : 0)
  const app = apps.find((a) => a.id === appId)

  const {
    data: webhook,
    isLoading,
    isError,
    refetch
  } = useQuery({
    ...settingsOutboundWebhooksWebhooksRetrieveOptions({
      path: { application_id: applicationId, id: numericWebhookId }
    }),
    enabled: applicationId > 0 && numericWebhookId > 0,
    // Same opt-out as the list: an edit made from the list drawer left this page showing the old
    // URL and event set, because the app-wide refetchOnMount:false serves the cached record.
    refetchOnMount: 'always'
  })

  const { update, remove, reveal } = useOutboundWebhookMutations(applicationId)
  const test = useWebhookTest(applicationId)
  const squadAgentUrlFor = useSquadAgentUrl(idValid ? workspaceId : 0)

  const [editing, setEditing] = useState(false)
  const [revealed, setRevealed] = useState<string | null>(null)

  // Fetched for the page, not just the edit form: it is what turns `lead.created` into a name a
  // human reads. Cached, and the list falls back to raw keys if it fails, so the page never blocks
  // on it.
  const catalogue = useEventCatalogue({
    domain: app?.domain,
    appToken: app?.token,
    enabled: true
  })

  const grouped = useMemo(
    () => groupEvents(webhook?.events ?? [], catalogue.data),
    [webhook?.events, catalogue.data]
  )

  if (!idValid || !appId || !webhookId || !applicationId) {
    return <Navigate to={paths.workspaces.apps.root(id)} replace />
  }

  const backTo = paths.workspaces.apps.outboundWebhooks(id, appId)

  const backButton = (
    <Link to={backTo}>
      <Button size="small" icon={<ArrowLeft size={14} />} type="text">
        {t('workspace.apps.outboundWebhookDetail.back', {
          // The apps list loads separately, so the name can still be missing here. Falling back
          // to the section name keeps the label a sentence instead of "Back to ".
          app: app?.name ?? t('shell.menu.apps'),
          defaultValue: 'Back to {{app}}'
        })}
      </Button>
    </Link>
  )

  const crumbs = [
    { title: t('shell.menu.apps'), href: paths.workspaces.apps.root(id) },
    ...(app
      ? [{ title: app.name, href: paths.workspaces.apps.details(id, appId) }]
      : []),
    { title: t('workspace.apps.nav.outboundWebhooks'), href: backTo }
  ]

  const leaf = t('workspace.apps.outboundWebhookDetail.breadcrumb', {
    defaultValue: 'Webhook'
  })

  if (isLoading) {
    return (
      <WorkspacePageShell breadcrumbExtras={crumbs} breadcrumbLeaf={leaf}>
        {backButton}
        <ContentSpinner minHeight={240} />
      </WorkspacePageShell>
    )
  }

  if (isError || !webhook) {
    return (
      <WorkspacePageShell breadcrumbExtras={crumbs} breadcrumbLeaf={leaf}>
        <Flex vertical gap={14}>
          {backButton}
          <QueryErrorAlert onRetry={() => void refetch()} />
        </Flex>
      </WorkspacePageShell>
    )
  }

  const handleEdit = async (values: WebhookFormValues) => {
    try {
      await update.mutateAsync({
        path: { application_id: applicationId, id: webhook.id },
        body: {
          name: values.name ?? '',
          url: values.url,
          events: values.events,
          is_active: values.is_active,
          // Omitted when empty so an edit never overwrites the stored secret with a blank.
          ...(values.custom_secret
            ? { custom_secret: values.custom_secret }
            : {})
        }
      })
      setEditing(false)
      await refetch()
    } catch (error) {
      showApiErrors(error)
    }
  }

  const handleDelete = () => {
    modal.confirm({
      title: t(
        'workspace.apps.outboundWebhooks.deleteModal.title',
        'Delete webhook?'
      ),
      content: t(
        'workspace.apps.outboundWebhooks.deleteModal.content',
        'Deliveries stop immediately, including attempts already queued for retry.'
      ),
      okText: t('button.delete', 'Delete'),
      cancelText: t('cancel', 'Cancel'),
      okButtonProps: { danger: true },
      onOk: () => performDelete()
    })
  }

  const performDelete = async () => {
    try {
      await remove.mutateAsync({
        path: { application_id: applicationId, id: webhook.id }
      })
      message.success(
        t('workspace.apps.outboundWebhooks.messages.deleted', 'Webhook deleted')
      )
      // Back to the list rather than an empty detail page for a row that no longer exists.
      navigate(backTo)
    } catch (error) {
      showApiErrors(error)
    }
  }

  const handleReveal = async () => {
    try {
      const result = await reveal.mutateAsync({
        path: { application_id: applicationId, id: webhook.id }
      })
      setRevealed(result.secret)
    } catch (error) {
      showApiErrors(error)
    }
  }

  // Composed from two already-pluralized fragments rather than one string with two numbers in it:
  // Russian and Romanian decline both halves, and a single `count` can only govern one of them.
  const eventSummary = t('workspace.apps.outboundWebhookDetail.eventSummary', {
    events: t('workspace.apps.outboundWebhooks.columns.eventCount', {
      count: webhook.events.length,
      defaultValue: '{{count}} events'
    }),
    entities: t('workspace.apps.outboundWebhookDetail.objectCount', {
      count: grouped.length,
      defaultValue: '{{count}} objects'
    }),
    defaultValue: '{{events}} across {{entities}}'
  })

  return (
    <WorkspacePageShell breadcrumbExtras={crumbs} breadcrumbLeaf={leaf}>
      <Flex vertical gap={16}>
        <Flex justify="space-between" align="center" wrap gap={8}>
          {backButton}
          <Flex gap={8}>
            <Button
              size="small"
              icon={<Zap size={14} />}
              loading={test.mutation.isPending}
              onClick={() => test.send(webhook.id)}
            >
              {t(
                'workspace.apps.outboundWebhooks.actions.test',
                'Send test webhook'
              )}
            </Button>
            <Button
              size="small"
              icon={<Eye size={14} />}
              loading={reveal.isPending}
              onClick={handleReveal}
            >
              {t(
                'workspace.apps.outboundWebhooks.actions.reveal',
                'Show signing secret'
              )}
            </Button>
            <Button
              size="small"
              icon={<Pencil size={14} />}
              onClick={() => setEditing(true)}
            >
              {t('button.edit', 'Edit')}
            </Button>
            <Button
              size="small"
              danger
              icon={<Trash2 size={14} />}
              loading={remove.isPending}
              onClick={handleDelete}
            >
              {t('button.delete', 'Delete')}
            </Button>
          </Flex>
        </Flex>

        <Card
          size="small"
          // State, not a property: as a Descriptions row it was a small pill next to a label,
          // between a url chip and two plain date rows, with nothing lining up. Beside the title
          // rather than in `extra`, where the card's width left it floating alone on the right.
          title={
            <Flex align="center" gap={8}>
              {t(
                'workspace.apps.outboundWebhookDetail.overviewTitle',
                'Overview'
              )}
              <Tag
                style={{ marginInlineEnd: 0 }}
                color={webhook.is_active ? 'success' : 'default'}
              >
                {webhook.is_active
                  ? t('workspace.apps.outboundWebhookDetail.stateActive', {
                      defaultValue: 'Active'
                    })
                  : t('workspace.apps.outboundWebhookDetail.stateInactive', {
                      defaultValue: 'Inactive'
                    })}
              </Tag>
            </Flex>
          }
        >
          {/* One column: two stretched to fill a wide viewport and left the Status tag half a
              screen from its label. */}
          <Descriptions
            column={1}
            size="small"
            colon={false}
            styles={{
              label: { width: LABEL_WIDTH, alignItems: 'center' },
              content: { alignItems: 'center' }
            }}
          >
            <Descriptions.Item
              label={t('workspace.apps.outboundWebhooks.columns.name')}
            >
              <WebhookName
                webhook={webhook}
                squadAgentUrl={squadAgentUrlFor(webhook)}
              />
            </Descriptions.Item>
            <Descriptions.Item
              label={t('workspace.apps.outboundWebhooks.columns.url')}
            >
              <MonoValue>{webhook.url}</MonoValue>
            </Descriptions.Item>
            <Descriptions.Item
              label={t('workspace.apps.outboundWebhooks.columns.created')}
            >
              <DateTimeCell value={webhook.date_created} variant="dateTime" />
            </Descriptions.Item>
            <Descriptions.Item
              label={t('workspace.apps.outboundWebhookDetail.updated', {
                defaultValue: 'Last changed'
              })}
            >
              <DateTimeCell value={webhook.date_updated} variant="dateTime" />
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card
          size="small"
          title={t(
            'workspace.apps.outboundWebhookDetail.eventsTitle',
            'Subscribed events'
          )}
        >
          {grouped.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t(
                    'workspace.apps.outboundWebhookDetail.noEvents',
                    'No events selected, so nothing is delivered.'
                  )}
                </Text>
              }
            />
          ) : (
            // The same grid the credential permission panel uses, for the same reason: one row per
            // entity, chips for what it covers.
            <LabeledTagGrid
              summary={eventSummary}
              rows={grouped.map((group) => ({
                key: group.entity,
                label: group.name,
                // The chip shows the human label; the key goes on the tooltip because that is the
                // value arriving in the X-CRM-Event header, so an integrator can still read it off.
                items: group.items.map((event) => ({
                  key: event.key,
                  label: event.label,
                  tooltip: event.key
                }))
              }))}
            />
          )}
        </Card>

        <SignatureCard />
      </Flex>

      <WebhookFormDrawer
        open={editing}
        webhook={webhook}
        catalogue={{
          groups: catalogue.data,
          isLoading: catalogue.isLoading,
          isError: catalogue.isError
        }}
        submitting={update.isPending}
        onSubmit={handleEdit}
        onClose={() => setEditing(false)}
      />

      <SigningSecretModal secret={revealed} onClose={() => setRevealed(null)} />

      <WebhookTestResultModal result={test.result} onClose={test.close} />
    </WorkspacePageShell>
  )
}
